import React, { useState, useRef, useImperativeHandle, forwardRef } from "react";
import DropDownButton from "./DropdownButton/DropdownButton.component";
import DropDownMenu from "./DropdownMenu.component";
import './Dropdown.component.scss';
import { HistoryIcon } from "../Icons";
import useOutsideClick from '../../hooks/useOutsideClick';

export type DropdownMenuItem = {
  name: string;
  path?: string;
  value: string;
  icon: React.ReactNode;
  color?: string;
  target?: string;
  // optional metadata handlers
  onClick?: () => void;
  disabled?: boolean;
};

export type DropDownProps = {
  label?: string;
  value?: string;
  itemList: DropdownMenuItem[];
  onChange?: (item: DropdownMenuItem) => void;
  defaultItem?: DropdownMenuItem | string;
};

type DropDownComponentProps = DropDownProps & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>;

const DropDown = forwardRef<HTMLDivElement, DropDownComponentProps>(
  function DropDown({ label, value, itemList = [], onChange, className = '', defaultItem, ...rest }, ref) {
    const [isActive, setIsActive] = useState<boolean>(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [selectedItem, setSelectedItem] = useState<DropdownMenuItem | null>(null);

    useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

    React.useEffect(() => {
      const foundItem = itemList?.find(item => item.value === value);
      if (foundItem) {
        setSelectedItem(foundItem);
      } else if (typeof defaultItem === "object") {
        setSelectedItem(defaultItem);
      } else if (typeof defaultItem === "string") {
        const foundDefaultItem = itemList.find(item => item.value === defaultItem);
        if (foundDefaultItem) {
          setSelectedItem(foundDefaultItem);
        }
      }
    }, [value, defaultItem, itemList]);
    useOutsideClick(containerRef as React.RefObject<HTMLElement>, () => setIsActive(false), {
      includeEscape: true,
    });

    const handleChange = (item: DropdownMenuItem) => {
      if (onChange) {
        onChange(item);
        setSelectedItem(item);
      }
      setIsActive(false);
    };

    const handleButtonClick = (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }
      setIsActive(!isActive);
    };

    // Extract data-tooltip from rest props
    const { 'data-tooltip': dataTooltip, ...restWithoutTooltip } = rest as any;
    
    return (
      <div className={`ui-dropdown ${className}`} ref={containerRef} {...restWithoutTooltip}>
        <DropDownButton
          color={selectedItem?.color}
          icon={selectedItem?.icon ?? <HistoryIcon />}
          label={selectedItem?.name || label || ''}
          onClick={handleButtonClick}
          isActive={isActive}
          data-tooltip={dataTooltip}
        />
        <DropDownMenu isActive={isActive} itemList={itemList} onChange={handleChange} />
      </div>
    );
  }
);

export default DropDown;
